/**
 * ConditionCaptureTab - predicate + overwrites editor.
 *
 * `condition` uses the shared ConditionBuilder sourced with refs from
 * the entity that maps an attribute value to this workflow.
 *
 * `overwrites` remains JSON-shaped until a later pass adds a
 * structured editor - documented in the plan as an intentional
 * fallback, not a TODO.
 */

import { useMemo } from 'react'

import { Label } from '@/shared/components/ui/label'

import { ConditionField } from './condition-field'
import { normalisePredicate } from './predicate-utils'
import { JsonObjectInput } from './json-object-input'
import { readForm, setFormField, type ActionFormContext } from './types'
import { useWorkflowConditionRefs } from './use-resource-refs'
import type { WorkflowOverwriteSpec } from '@/admin/hooks/use-workflow-definitions'

interface ConditionCaptureTabProps {
  formCtx: ActionFormContext
  workflowKey: string
  resourceType?: string
}

export function ConditionCaptureTab({
  formCtx,
  workflowKey,
  resourceType,
}: ConditionCaptureTabProps) {
  const form = readForm(formCtx)
  const { refs, byRef, isLoading } = useWorkflowConditionRefs({
    workflowKey,
    resourceType,
  })

  const overwritesJson = useMemo(
    () => JSON.stringify(form.overwrites ?? {}, null, 2),
    [form.overwrites],
  )

  return (
    <div className="flex flex-col gap-5">
      <ConditionField
        label="Condition"
        hint="When the predicate evaluates to true, this action is eligible. Leave empty to make it unconditional."
        value={normalisePredicate(form.condition)}
        onChange={(next) => setFormField(formCtx, 'condition', next)}
        refsLoading={isLoading}
        refs={refs}
        byRef={byRef}
      />

      <div className="flex flex-col gap-1.5">
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Overwrites (JSON object)
        </Label>
        <p className="text-[10px] text-[var(--ink-3)]">
          Entity columns to overwrite when this action fires. Shape:{' '}
          <code className="font-mono">
            {'{"column": {"literal": X} | {"attribute": "code_name"}}'}
          </code>
          .
        </p>
        <JsonObjectInput
          key={overwritesJson}
          value={overwritesJson}
          onCommit={(next) =>
            setFormField(
              formCtx,
              'overwrites',
              next as Record<string, WorkflowOverwriteSpec>,
            )
          }
        />
      </div>
    </div>
  )
}
